import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { kycApi } from '@/api/kycApi';
import type { KycData } from '../config/kycSteps.config';
import { calculateProgress, getActiveSteps } from '../config/kycSteps.config';
import { useAuth } from '@/features/auth/hooks/useAuth';

export const useKycFlow = (initialData?: KycData, initialStepId?: string) => {
  const { user: currentUser, refetchUser } = useAuth();
  const [kycData, setKycData] = useState<KycData>(initialData || {});
  const steps = getActiveSteps();
    const [currentStepId, setCurrentStepId] = useState<string>(() => initialStepId || steps[0]?.id || 'auth-method');
  const [sessionId] = useState(() => uuidv4());
  const [isLoading, setIsLoading] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
  const progress = calculateProgress(kycData);

  useEffect(() => {
      // Only initialize once - prevent resetting when navigating between steps
      if (hasInitialized) return;

    const loadKycData = async () => {
        const activeSteps = getActiveSteps();

      // CRITICAL: Only fetch KYC data if user is authenticated
      if (!currentUser) {
        // Unauthenticated: start at auth-method step, no data to load
          setCurrentStepId(initialStepId || 'auth-method');
          setHasInitialized(true);
        return;
      }

      try {
        const response = await kycApi.getKycData();
        setKycData(response.user);

        // Find first incomplete step based on validation
          const firstIncomplete = activeSteps.find((s) => !s.validation(response.user));
        if (firstIncomplete) {
            setCurrentStepId(initialStepId || firstIncomplete.id);
        } else {
            setCurrentStepId(initialStepId || 'complete');
        }
          setHasInitialized(true);
      } catch (error) {
        console.error('Failed to load KYC data:', error);
        // For authenticated users, preserve context by starting at first step after auth
          const firstNonAuthStep = activeSteps.find((s) => s.id !== 'auth-method');
          setCurrentStepId(initialStepId || firstNonAuthStep?.id || 'account');
          setHasInitialized(true);
      }
    };

    if (initialData && !currentUser) {
      // Only use initialData if user is not authenticated yet
      setKycData(initialData);

      // Use provided initialStepId or calculate first incomplete
      if (initialStepId) {
        setCurrentStepId(initialStepId);
      } else {
          const activeSteps = getActiveSteps();
          const firstIncomplete = activeSteps.find((s) => !s.validation(initialData));
        setCurrentStepId(firstIncomplete?.id || 'complete');
      }
        setHasInitialized(true);
    } else {
      // Once user is authenticated, always fetch fresh data
      loadKycData();
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateKycData = useCallback(async (updates: KycData) => {
    setKycData((prev) => ({ ...prev, ...updates }));

    // CRITICAL: Only send API request if user is authenticated
    if (!currentUser) {
      // For unauthenticated users, just update local state
      return;
    }

    try {
      setIsLoading(true);
      await kycApi.updateKycData(updates, sessionId);
      // Refetch user to update KYC progress in header
      await refetchUser();
    } catch (error) {
      console.error('Failed to save KYC data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, sessionId, refetchUser]);

  const moveToNextIncompleteStep = useCallback(async (pendingUpdates?: KycData) => {
    // Persist to backend if there are updates (handles auth check internally)
    if (pendingUpdates) {
      await updateKycData(pendingUpdates);
    }

    // Find next incomplete step based on validation
    setKycData((currentData) => {
      // Merge pending updates with current data to avoid race conditions
      const dataToValidate = pendingUpdates ? { ...currentData, ...pendingUpdates } : currentData;

      // Check if current step was skipped (emailVerificationStatus=SKIPPED or addressSkipped=true)
      const wasSkipped =
          (dataToValidate.emailVerificationStatus === 'SKIPPED' && currentStepId === 'verify-email') ||
          (dataToValidate.addressSkipped === true && currentStepId === 'address');

      let nextStepId: string;

      if (wasSkipped) {
        // If current step was skipped, move to next step in sequence (not next incomplete)
        const currentIndex = steps.findIndex((s) => s.id === currentStepId);
        if (currentIndex >= 0 && currentIndex < steps.length - 1) {
          nextStepId = steps[currentIndex + 1].id;
        } else {
          nextStepId = 'complete';
        }
      } else {
        // Normal flow: find first incomplete step
        const nextIncomplete = steps.find((s) => !s.validation(dataToValidate));
        nextStepId = nextIncomplete ? nextIncomplete.id : 'complete';
      }

      setCurrentStepId(nextStepId);

      // Return merged data to apply pending updates
      return dataToValidate;
    });
  }, [steps, updateKycData, currentStepId]);

  const goToStep = useCallback((stepId: string) => {
    setCurrentStepId(stepId);
  }, []);

  return {
    kycData,
    updateKycData,
    currentStepId,
    setCurrentStepId: goToStep,
    steps,
    progress,
    moveToNextIncompleteStep,
    sessionId,
    isLoading,
  };
};
